// AGC: "What users love" — top features, entity types created, and creation sources.
import { useEffect, useState } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import { InsightCard } from './InsightCard'
import {
  getTopFeatures,
  getEntityCreations,
  getCreationSources,
  type DateRange,
  type FeatureUsageRow,
  type EntityCreationRow,
  type CreationSourceRow,
} from '../../../services/analyticsQueryService'
import {
  interpretTopFeatures,
  interpretEntityCreations,
  interpretCreationSources,
} from '../../../services/analyticsInsightGenerator'
import { useTranslation } from '../../../hooks/useTranslation'

interface PopularitySectionProps {
  range: DateRange
}

// AGC: Palette for the creation-source pie chart slices (emerald, blue, amber, red, purple)
const PIE_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'] as const

// AGC: Shared skeleton placeholder for loading state
function LoadingBox(): JSX.Element {
  return <div className="h-48 bg-slate-100 dark:bg-white/[0.04] animate-pulse rounded" />
}

// AGC: Shared "no data" placeholder
function EmptyState({ message }: { message: string }): JSX.Element {
  return (
    <div className="text-sm text-slate-500 dark:text-slate-400 h-48 flex items-center justify-center">
      {message}
    </div>
  )
}

export function PopularitySection({ range }: PopularitySectionProps): JSX.Element {
  const [features, setFeatures] = useState<FeatureUsageRow[] | null>(null)
  const [entities, setEntities] = useState<EntityCreationRow[] | null>(null)
  const [sources, setSources] = useState<CreationSourceRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()

  useEffect(() => {
    // AGC: Cancel flag prevents setState on unmounted component
    let cancelled = false
    setLoading(true)
    Promise.all([
      getTopFeatures(range, 10),
      getEntityCreations(range),
      getCreationSources(range),
    ])
      .then(([f, e, s]) => {
        if (cancelled) return
        setFeatures(f)
        setEntities(e)
        setSources(s)
      })
      .catch((err) => { console.warn('[analytics-dashboard] popularity failed', err) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [range])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* AGC: Top features — horizontal bar chart, Y axis = feature name, X axis = count */}
      <InsightCard
        title={t('admin.analytics.card.topFeatures')}
        insight={features ? interpretTopFeatures(features) : undefined}
        helpDescription="Features ranked by how often they're triggered."
        helpGoodSignal="One feature clearly dominates — promote it."
        helpAction="Features with zero use may be dead weight. Consider removing or redesigning."
      >
        {loading || !features ? (
          <LoadingBox />
        ) : features.length === 0 ? (
          <EmptyState message={t('admin.analytics.empty.noFeatures')} />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={features} layout="vertical" margin={{ left: 40, right: 16, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="feature_name" width={110} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#10b981" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </InsightCard>

      {/* AGC: Entity creations — vertical bar chart, X axis = entity_type, Y axis = count */}
      <InsightCard
        title={t('admin.analytics.card.entitiesCreated')}
        insight={entities ? interpretEntityCreations(entities) : undefined}
        helpDescription="How many of each entity type (task, vehicle, plot, etc.) users created."
        helpGoodSignal="Core entities get created frequently."
        helpAction="Entities with very low counts may have UX problems."
      >
        {loading || !entities ? (
          <LoadingBox />
        ) : entities.length === 0 ? (
          <EmptyState message={t('admin.analytics.empty.noEntities')} />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={entities} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="entity_type" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </InsightCard>

      {/* AGC: Creation sources — pie chart, each slice = one source (voice/form/wizard/etc.) */}
      <InsightCard
        title={t('admin.analytics.card.creationSource')}
        insight={sources ? interpretCreationSources(sources) : undefined}
        helpDescription="How users create entities — voice, form, wizard, QR scan, or WhatsApp."
        helpGoodSignal="One source clearly dominates — that's your core flow."
        helpAction="Low-usage sources may be broken or hard to find."
      >
        {loading || !sources ? (
          <LoadingBox />
        ) : sources.length === 0 ? (
          <EmptyState message={t('admin.analytics.empty.noEntities')} />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={sources}
                dataKey="count"
                nameKey="source"
                cx="50%"
                cy="45%"
                outerRadius={75}
                label={(entry: { source: string; count: number }) => `${entry.source} (${entry.count})`}
                labelLine={true}
              >
                {sources.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </InsightCard>
    </div>
  )
}
